import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import DesktopLayout from '../../../layout/DesktopLayout';
import BottomNav from '../../../BottomNav';
import { Bone } from '../../../Shimmer';
import { useCateringAccess } from '../../../../redux/catering/cateringAccessHooks';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useCateringToastStore } from '../../store/useToast';
import { canEditArea, canOpenArea, firstCateringPath, NO_PERMISSION_MESSAGE, type CateringArea } from '../../utils/cateringAccess';
import CateringAccessRestricted from './CateringAccessRestricted';

/** The page frame with a shimmer while the permissions are read for the first time. */
function CateringAccessLoading() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const content = (
    <div aria-busy="true" data-testid="catering-access-loading" style={{ padding: isMobile ? 16 : 32 }}>
      <Bone w={220} h={24} mb={10} />
      <Bone w={320} h={12} mb={24} />
      {[0, 1, 2, 3].map((i) => <Bone key={i} h={56} r={14} mb={10} />)}
    </div>
  );
  if (isMobile) return <div style={{ minHeight: '100vh', paddingBottom: 80 }}>{content}<BottomNav /></div>;
  return <DesktopLayout>{content}</DesktopLayout>;
}

/** A view-only employee opened a create / edit page: open the record read-only instead and say why. */
function ViewOnlyRedirect({ to }: { to: string }) {
  const { addToast } = useCateringToastStore();
  const told = useRef(false);
  useEffect(() => {
    if (told.current) return;
    told.current = true;
    addToast(NO_PERMISSION_MESSAGE, 'error');
  }, [addToast]);
  return <Navigate to={to} replace />;
}

interface CateringAccessGateProps {
  /** Which part of Catering the page belongs to. */
  area: CateringArea;
  /**
   * Set on create / edit pages: they need the area's EDIT permission. A view-only employee is sent to this page
   * instead (the record's detail, or the list for a create page) with a "You don't have permission" toast.
   */
  viewOnlyFallback?: (params: Readonly<Record<string, string | undefined>>) => string;
  children: ReactNode;
}

/**
 * Route guard for the Catering pages (direct URLs too). Permissions are read again on every visit and refresh,
 * so a change made in Employees applies without logging in again. Without the area's permission the Access
 * Restricted page is shown.
 */
export function CateringAccessGate({ area, viewOnlyFallback, children }: CateringAccessGateProps) {
  const access = useCateringAccess();
  const params = useParams();
  const { load } = access;

  useEffect(() => {
    load();
  }, [load]);

  if (access.status !== 'ready') return <CateringAccessLoading />;
  if (!canOpenArea(access, area)) return <CateringAccessRestricted />;
  if (viewOnlyFallback && !canEditArea(access, area)) return <ViewOnlyRedirect to={viewOnlyFallback(params)} />;
  return <>{children}</>;
}

/**
 * The Catering side-nav parent's landing: the first sub-page in nav order (Leads, Estimates, Orders, Invoices,
 * Customers, Packages, Settings) the employee can open, or the Access Restricted page when there is none.
 */
export function CateringLanding() {
  const access = useCateringAccess();
  const { load } = access;

  useEffect(() => {
    load();
  }, [load]);

  if (access.status !== 'ready') return <CateringAccessLoading />;
  const first = firstCateringPath(access);
  return first ? <Navigate to={first} replace /> : <CateringAccessRestricted />;
}
